/**
 * Move generation, attack detection and legality.
 *
 * Implements RULES.md. Two structural differences from the 2023 build, both of which
 * the perft goldens are there to prove behaviour-preserving:
 *
 *   1. Moves are generated once, without duplicates. The old generator emitted the
 *      one-cell move twice whenever a bow was live, because the glide ray starts on the
 *      cell an ordinary step also reaches (§4.1). Here the step is emitted by the step
 *      branch and the ray simply starts one cell further along.
 *
 *   2. Legality uses the equivalent cheap form from §6.1 instead of enumerating every
 *      opponent reply to every candidate move. With two or more kings alive nothing
 *      needs checking at all; with exactly one, it is a single reverse-attack query.
 */

import { OFF_BOARD } from './board.ts';
import { DIRECTION_COUNT, opposite } from './hex.ts';
import {
  type FastPosition,
  isKing,
  isPawn,
  makeMove,
  MAX_KINGS,
  MOVE_PASS,
  MOVE_SPAWN,
  MOVE_STEP,
  moveFrom,
  moveTo,
  moveType,
  ownerOf,
  packMove,
  unmakeMove,
} from './position.ts';
import type { Move, PlayerIndex } from './types.ts';

/**
 * A reusable move buffer. Generation writes packed moves here rather than allocating an
 * array per node, which is what makes a deep search affordable.
 *
 * The bound is generous: the densest reachable position is far below it.
 */
export const MAX_MOVES = 512;

export class MoveList {
  readonly moves = new Int32Array(MAX_MOVES);
  length = 0;

  clear(): void {
    this.length = 0;
  }

  push(move: number): void {
    if (this.length >= MAX_MOVES) {
      throw new Error(`Move list overflow: more than ${MAX_MOVES} moves generated`);
    }
    this.moves[this.length++] = move;
  }

  at(i: number): number {
    return this.moves[i];
  }

  toArray(): number[] {
    return Array.from(this.moves.subarray(0, this.length));
  }
}

/**
 * Whether the piece at `from` can fire a bow in direction `dir` (RULES.md §4).
 *
 * Requires friendly pieces at `(dir+2)%6` and `(dir+4)%6`, and an on-board *empty* cell
 * at `(dir+3)%6` — the cell directly behind the glider.
 */
/**
 * Whether a bow is live for `player` firing from `from` in direction `dir` (§4).
 *
 * Exported because an interface has to be able to say *why* a move is legal, not just
 * that it is — see `analysis.ts`. It stays the only place the condition is decided.
 */
export function hasBow(
  pos: FastPosition,
  from: number,
  dir: number,
  player: PlayerIndex,
): boolean {
  const { board, cells } = pos;

  const flankA = board.neighborOf(from, (dir + 2) % 6);
  if (flankA === OFF_BOARD) return false;
  const a = cells[flankA];
  if (a === 0 || ownerOf(a) !== player) return false;

  const behind = board.neighborOf(from, (dir + 3) % 6);
  if (behind === OFF_BOARD || cells[behind] !== 0) return false;

  const flankB = board.neighborOf(from, (dir + 4) % 6);
  if (flankB === OFF_BOARD) return false;
  const b = cells[flankB];
  if (b === 0 || ownerOf(b) !== player) return false;

  return true;
}

/** All pseudo-legal moves for the player to move, written into `out`. */
export function generatePseudoMoves(pos: FastPosition, out: MoveList): void {
  const { board, cells, spawns } = pos;
  const me = pos.playerToMove;

  out.clear();
  out.push(packMove(MOVE_PASS));

  const canSpawn = spawns[me] > 0;

  for (let from = 0; from < cells.length; from++) {
    const piece = cells[from];
    if (piece === 0 || ownerOf(piece) !== me) continue;

    const fromIsKing = isKing(piece);
    const fromIsPawn = !fromIsKing;

    for (let dir = 0; dir < DIRECTION_COUNT; dir++) {
      const to = board.neighborOf(from, dir);
      if (to === OFF_BOARD) continue;

      const target = cells[to];

      if (target === 0) {
        out.push(packMove(MOVE_STEP, from, to));
        // Spawns place a pawn on an empty cell adjacent to one of your kings (§5).
        if (fromIsKing && canSpawn) out.push(packMove(MOVE_SPAWN, from, to));
      } else if (ownerOf(target) !== me) {
        // A pawn may not capture a pawn (§3). Everything else may capture anything.
        if (!(fromIsPawn && isPawn(target))) {
          out.push(packMove(MOVE_STEP, from, to));
        }
      }

      // The bow (§4). Note the bow's own conditions say nothing about the cell being
      // launched towards, so a bow can be live even when the adjacent cell is occupied.
      if (!hasBow(pos, from, dir, me)) continue;

      if (target !== 0) {
        // The ray stops at the first occupied cell, which here is the adjacent one.
        // The step branch above already emitted this move — except in the single case
        // it is required to refuse: a pawn taking a pawn. The glide is exempt from that
        // restriction (§4), so it is emitted here, exactly once (§4.1).
        if (ownerOf(target) !== me && fromIsPawn && isPawn(target)) {
          out.push(packMove(MOVE_STEP, from, to));
        }
        continue;
      }

      // The adjacent cell is empty and was already emitted as a step, so the ray picks
      // up one cell further along — emitting it again is the duplicate the 2023
      // generator produced (§4.1).
      let ray = board.neighborOf(to, dir);
      while (ray !== OFF_BOARD) {
        const hit = cells[ray];
        if (hit === 0) {
          out.push(packMove(MOVE_STEP, from, ray));
        } else {
          // A glider may capture anything, pawns included — the sole exemption from
          // the pawn-versus-pawn rule (§4). It may not take a friendly piece.
          if (ownerOf(hit) !== me) out.push(packMove(MOVE_STEP, from, ray));
          break;
        }
        ray = board.neighborOf(ray, dir);
      }
    }
  }
}

/**
 * Whether `player` has a pseudo-move that lands on `cell`.
 *
 * Used only to ask whether a king can be captured, so it deliberately ignores the
 * pawn-versus-pawn restriction: a king is capturable by anything (§3). Spawns place
 * onto empty cells and so can never capture, and passing captures nothing — steps and
 * glides are the whole story.
 *
 * Replaces the 2023 approach of generating every opponent reply and applying each one
 * to a fresh board copy.
 */
export function isAttackedBy(
  pos: FastPosition,
  cell: number,
  player: PlayerIndex,
): boolean {
  const { board, cells } = pos;

  // Adjacent attackers.
  for (let dir = 0; dir < DIRECTION_COUNT; dir++) {
    const n = board.neighborOf(cell, dir);
    if (n === OFF_BOARD) continue;
    const c = cells[n];
    if (c !== 0 && ownerOf(c) === player) return true;
  }

  // Gliding attackers. For each direction `dir` a glider could be travelling, walk
  // backwards along the ray. The first occupied cell found is the only possible
  // glider — anything beyond it is blocked — so the walk stops there either way.
  for (let dir = 0; dir < DIRECTION_COUNT; dir++) {
    const back = opposite(dir);
    let at = board.neighborOf(cell, back);

    while (at !== OFF_BOARD) {
      const c = cells[at];
      if (c !== 0) {
        if (ownerOf(c) === player && hasBow(pos, at, dir, player)) return true;
        break;
      }
      at = board.neighborOf(at, back);
    }
  }

  return false;
}

/**
 * Whether the move just made left the mover's king safe, per RULES.md §6.1.
 * Call with the position *after* the move, and `mover` being who made it.
 */
function moverKingSurvives(pos: FastPosition, mover: PlayerIndex): boolean {
  const kings = pos.kingCount[mover];

  // Two or more kings: no single reply can reduce the mover to zero, so the move is
  // legal without further checking. This is what makes diarchy, triarchy and colonies
  // nearly free to generate.
  if (kings >= 2) return true;

  // No kings: already lost. Every move from here is illegal, which is how checkmate
  // reports itself.
  if (kings === 0) return false;

  return !isAttackedBy(pos, pos.kingCells[mover * MAX_KINGS], (mover ^ 1) as PlayerIndex);
}

/**
 * Scratch buffers for the pseudo-move list inside `generateLegalMoves`.
 *
 * A pool with a depth pointer rather than a single buffer, so nesting is safe, and
 * rather than `new MoveList()` per call, which would allocate once per node visited and
 * dominate any search.
 */
const scratchPool: MoveList[] = [];
let scratchDepth = 0;

/** All legal moves for the player to move, written into `out`. */
export function generateLegalMoves(pos: FastPosition, out: MoveList): void {
  const pseudo = scratchPool[scratchDepth] ??
    (scratchPool[scratchDepth] = new MoveList());
  scratchDepth++;
  try {
    generateLegalInto(pos, pseudo, out);
  } finally {
    scratchDepth--;
  }
}

function generateLegalInto(pos: FastPosition, pseudo: MoveList, out: MoveList): void {
  generatePseudoMoves(pos, pseudo);

  const mover = pos.playerToMove;
  out.clear();

  for (let i = 0; i < pseudo.length; i++) {
    const move = pseudo.at(i);
    const undo = makeMove(pos, move);
    const ok = moverKingSurvives(pos, mover);
    unmakeMove(pos, move, undo);
    if (ok) out.push(move);
  }
}

/** Whether the player to move has no legal move — checkmate (§6). */
export function isCheckmate(pos: FastPosition): boolean {
  const list = new MoveList();
  generateLegalMoves(pos, list);
  return list.length === 0;
}

/**
 * Whether the player to move has exactly one king and it is currently attacked —
 * "check". Not itself a rule, but the interface needs it and so does the bot's
 * evaluation.
 */
export function isInCheck(pos: FastPosition): boolean {
  const me = pos.playerToMove;
  if (pos.kingCount[me] !== 1) return false;
  return isAttackedBy(pos, pos.kingCells[me * MAX_KINGS], (me ^ 1) as PlayerIndex);
}

// --- object-shaped API ----------------------------------------------------

export const unpackMove = (m: number): Move => {
  switch (moveType(m)) {
    case MOVE_PASS:
      return { type: 'pass' };
    case MOVE_SPAWN:
      return { type: 'spawnPiece', fromIndex: moveFrom(m), toIndex: moveTo(m) };
    case MOVE_STEP:
      return { type: 'movePiece', fromIndex: moveFrom(m), toIndex: moveTo(m) };
    default:
      return { type: 'resign' };
  }
};

export const packFromMove = (move: Move): number => {
  switch (move.type) {
    case 'pass':
      return packMove(MOVE_PASS);
    case 'resign':
      return packMove(3);
    case 'spawnPiece':
      return packMove(MOVE_SPAWN, move.fromIndex, move.toIndex);
    case 'movePiece':
      return packMove(MOVE_STEP, move.fromIndex, move.toIndex);
  }
};

/** Legal moves as plain objects, for the wire and the UI. */
export function legalMoves(pos: FastPosition): Move[] {
  const list = new MoveList();
  generateLegalMoves(pos, list);
  const out: Move[] = [];
  for (let i = 0; i < list.length; i++) out.push(unpackMove(list.at(i)));
  return out;
}

/** Pseudo-legal moves as plain objects. Mostly useful in tests. */
export function pseudoMoves(pos: FastPosition): Move[] {
  const list = new MoveList();
  generatePseudoMoves(pos, list);
  const out: Move[] = [];
  for (let i = 0; i < list.length; i++) out.push(unpackMove(list.at(i)));
  return out;
}

/** Whether a specific move is legal in this position. */
export function isLegal(pos: FastPosition, move: Move): boolean {
  const target = packFromMove(move);
  const list = new MoveList();
  generateLegalMoves(pos, list);
  for (let i = 0; i < list.length; i++) {
    if (list.at(i) === target) return true;
  }
  return false;
}
